import { ExtendedBrand } from './BrandTypes';
import { fetchBrandDocument } from '../../services/brand';

// Cached document URLs to avoid redundant API calls
const documentCache: Record<string, Promise<string | null>> = {};

// Use reliable placeholder images that won't fail to load
const NO_IMAGE_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200' viewBox='0 0 300 200'%3E%3Crect width='300' height='200' fill='%23f1f1f1'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='16' text-anchor='middle' dominant-baseline='middle' fill='%23999'%3ENo Image Available%3C/text%3E%3C/svg%3E";
const NO_CERTIFICATE_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200' viewBox='0 0 300 200'%3E%3Crect width='300' height='200' fill='%23f1f1f1'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='16' text-anchor='middle' dominant-baseline='middle' fill='%23999'%3ENo Certificate Available%3C/text%3E%3C/svg%3E";

/**
 * Checks if a string is a full URL (starts with http:// or https://)
 */
const isFullUrl = (url: string): boolean => {
  return url.startsWith('http://') || url.startsWith('https://');
};

// Get image URL from Brand, with API fetch if needed
export const getImageUrl = (Brand: ExtendedBrand | null, type: 'shopFront' | 'shopInner' | 'evCer'): string => {
  if (!Brand) return type === 'evCer' ? NO_CERTIFICATE_PLACEHOLDER : NO_IMAGE_PLACEHOLDER;
  
  console.log(`[DEBUG] getImageUrl called for Brand ${Brand.name || Brand._id}, docType: ${type}`);
  
  // Check if we should try fetching from API
  if (Brand.mobileNo) {
    const cacheKey = `${Brand.mobileNo}-${type}`;
    
    // Try to get from kycDocs first to check if it's a valid URL
    let shouldFetchFromApi = true;
    
    if (Brand.kycDocs && Brand.kycDocs[type]) {
      const url = Brand.kycDocs[type] as string;
      console.log(`[DEBUG] Found URL in kycDocs: ${url}`);
      
      // Only use the URL from kycDocs if it's a full URL
      if (isFullUrl(url)) {
        console.log(`[DEBUG] URL from kycDocs is a full URL, using it directly`);
        shouldFetchFromApi = false;
        return url;
      } else {
        console.log(`[DEBUG] URL from kycDocs is not a full URL, will fetch from API`);
      }
    } else if (Brand.kyc && Brand.kyc.docs && typeof Brand.kyc.docs === 'object') {
      const docsObj = Brand.kyc.docs as any;
      if (docsObj[type]) {
        const url = docsObj[type];
        console.log(`[DEBUG] Found URL in kyc.docs: ${url}`);
        
        // Only use the URL from kyc.docs if it's a full URL
        if (isFullUrl(url)) {
          console.log(`[DEBUG] URL from kyc.docs is a full URL, using it directly`);
          shouldFetchFromApi = false;
          return url;
        } else {
          console.log(`[DEBUG] URL from kyc.docs is not a full URL, will fetch from API`);
        }
      }
    }
    
    // If we need to fetch from API
    if (shouldFetchFromApi) {
      console.log(`[DEBUG] Will try API fetch for ${Brand.mobileNo}-${type}`);
      
      // Check if we already have a fetch in progress
      if (!documentCache[cacheKey]) {
        console.log(`[DEBUG] Starting new fetch for ${cacheKey}`);
        // Start fetch in background and store promise in cache
        documentCache[cacheKey] = (async () => {
          try {
            // Get token from localStorage (not ideal, but works)
            const token = localStorage.getItem('token');
            console.log(`[DEBUG] Token available: ${!!token}`);
            
            // Fetch document from API
            console.log(`[DEBUG] Calling fetchBrandDocument(${Brand.mobileNo}, ${type}, ${!!token}, true)`);
            const url = await fetchBrandDocument(
              Brand.mobileNo,
              type,
              token,
              true // Using mobile number
            );
            
            if (url) {
              console.log(`[DEBUG] API fetch result: Success, URL: ${url}`);
              return url;
            } else {
              console.log(`[DEBUG] API fetch result: No URL returned`);
              return null;
            }
          } catch (error) {
            console.error(`[DEBUG] Error fetching ${type} document for Brand ${Brand.mobileNo}:`, error);
            return null;
          }
        })();
        
        // Execute the promise and update UI when done
        documentCache[cacheKey].then(url => {
          if (url) {
            console.log(`[DEBUG] Updating images for ${cacheKey} with URL: ${url}`);
            // Force component re-render with DOM update
            const images = document.querySelectorAll(`img[data-Brand-doc="${Brand.mobileNo}-${type}"]`);
            console.log(`[DEBUG] Found ${images.length} images to update for ${cacheKey}`);
            images.forEach(img => {
              console.log(`[DEBUG] Updating image src to: ${url}`);
              (img as HTMLImageElement).src = url;
            });
          }
        });
      } else {
        console.log(`[DEBUG] Fetch already in progress for ${cacheKey}`);
      }
      
      // Return placeholder while fetch is in progress
      console.log(`[DEBUG] Returning placeholder for ${type} while fetch is in progress`);
      return type === 'evCer' ? NO_CERTIFICATE_PLACEHOLDER : NO_IMAGE_PLACEHOLDER;
    }
  } else {
    console.log(`[DEBUG] Brand has no mobileNo, cannot fetch document`);
  }
  
  console.log(`[DEBUG] Returning placeholder for ${type}`);
  // Return placeholder if nothing found
  return type === 'evCer' ? NO_CERTIFICATE_PLACEHOLDER : NO_IMAGE_PLACEHOLDER;
};

// Check if document exists in either kycDocs or kyc.docs
export const hasDocument = (Brand: ExtendedBrand | null, type: 'shopFront' | 'shopInner' | 'evCer'): boolean => {
  if (!Brand) return false;
  
  // Check in kycDocs
  if (Brand.kycDocs && Brand.kycDocs[type]) {
    return true;
  }
  
  // Check in kyc.docs if it's an object
  if (Brand.kyc && Brand.kyc.docs && typeof Brand.kyc.docs === 'object') {
    const docsObj = Brand.kyc.docs as any;
    return !!docsObj[type];
  }
  
  return false;
};

// Generate QR code URL
export const getQrCodeUrl = (token?: string) => {
  if (!token) return 'https://merchant.cleverpe.com/register/';
  return `https://merchant.cleverpe.com/register/${token}`;
}; 